import React, { useState } from 'react';
import {
  GitBranch, LayoutTemplate, Brain, ListChecks, FileCode2, Sparkles,
  Bell, Volume2, Github, Gitlab, Cloud, Box, Server, BookOpen, Database,
  Plug, Plus, Trash2, Save, X as XIcon, CircleCheck, AlertTriangle, ExternalLink,
} from 'lucide-react';
import Toggle from './Toggle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-6">
    <div className="w-8 h-8 rounded-md bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-cyan-300" />
    </div>
    <div>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      {subtitle && <p className="text-xs text-zinc-500 mt-0.5 max-w-xl">{subtitle}</p>}
    </div>
  </div>
);

const FeatureRow = ({ icon: Icon, title, description, checked, onChange, testId }) => (
  <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4">
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 text-zinc-400 shrink-0" />
        <span className="text-sm font-medium text-white truncate">{title}</span>
      </div>
      <Toggle checked={checked} onChange={onChange} testId={testId} />
    </div>
    <p className="text-xs text-zinc-500 leading-relaxed">{description}</p>
  </div>
);

// ─── FEATURES ────────────────────────────────────────────────────────────────
export function FeaturesSection({ settings, save }) {
  const [tab, setTab] = useState('core');
  const core = settings.core || {};
  const beta = settings.beta || {};
  const library = settings.prompt_library || [];
  const [newPrompt, setNewPrompt] = useState({ title: '', body: '' });

  return (
    <div>
      <div className="flex items-center gap-6 border-b border-white/[0.06] mb-6">
        {[
          { id: 'core',    label: 'Core Features'  },
          { id: 'beta',    label: 'Beta Features'  },
          { id: 'library', label: 'Prompt Library' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`feat-tab-${t.id}`}
            className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'text-cyan-300 border-cyan-400' : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'core' && (
        <div>
          <div className="flex items-center gap-2 mb-5">
            <CircleCheck className="w-5 h-5 text-cyan-400" />
            <div>
              <h4 className="text-sm font-semibold">Core Features</h4>
              <p className="text-xs text-zinc-500">Essential features enabled by default for optimal performance.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FeatureRow
              icon={GitBranch}
              title="Main Branch Updates"
              description="Pull the latest Snagcart Builder template updates from the main branch when a new project is created."
              checked={!!core.main_branch_updates}
              onChange={(v) => save({ core: { main_branch_updates: v } })}
              testId="toggle-main-branch-updates"
            />
            <FeatureRow
              icon={LayoutTemplate}
              title="Auto Select Template"
              description="Automatically pick the most appropriate starter template based on your prompt, no manual selection needed."
              checked={!!core.auto_select_template}
              onChange={(v) => save({ core: { auto_select_template: v } })}
              testId="toggle-auto-select-template"
            />
            <FeatureRow
              icon={Brain}
              title="Context Optimization"
              description="Compress chat & file context for cheaper, faster, higher-quality AI responses."
              checked={!!core.context_optimization}
              onChange={(v) => save({ core: { context_optimization: v } })}
              testId="toggle-context-optimization"
            />
            <FeatureRow
              icon={ListChecks}
              title="Event Logging"
              description="Record detailed logs of every build step, agent thought and chat event in the right-side stream."
              checked={!!core.event_logging}
              onChange={(v) => save({ core: { event_logging: v } })}
              testId="toggle-event-logging"
            />
            <FeatureRow
              icon={FileCode2}
              title="Auto-Switch to File During AI Edits"
              description="When the AI edits a file, jump to the Files tab so you can see the change in real time."
              checked={!!core.auto_switch_to_file}
              onChange={(v) => save({ core: { auto_switch_to_file: v } })}
              testId="toggle-auto-switch-to-file"
            />
            <FeatureRow
              icon={Sparkles}
              title="Extended Thinking"
              description="Enable longer reasoning for supported models (Claude, Gemini). Uses ~25% more output tokens for deeper analysis."
              checked={!!core.extended_thinking}
              onChange={(v) => save({ core: { extended_thinking: v } })}
              testId="toggle-extended-thinking"
            />
          </div>
        </div>
      )}

      {tab === 'beta' && (
        <div>
          <div className="flex items-center gap-2 mb-5">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div>
              <h4 className="text-sm font-semibold">Beta Features</h4>
              <p className="text-xs text-zinc-500">Experimental — may change or break. Opt in at your own risk.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FeatureRow
              icon={Sparkles}
              title="Experimental Models"
              description="Route builds through pre-release models (GPT-5.2 preview, Claude Opus 4.5) where available."
              checked={!!beta.experimental_models}
              onChange={(v) => save({ beta: { experimental_models: v } })}
              testId="toggle-experimental-models"
            />
            <FeatureRow
              icon={GitBranch}
              title="Parallel Agent Runs"
              description="Run multiple autonomous vision iterations side-by-side and merge the winning result."
              checked={!!beta.parallel_agent_runs}
              onChange={(v) => save({ beta: { parallel_agent_runs: v } })}
              testId="toggle-parallel-agent-runs"
            />
            <FeatureRow
              icon={Volume2}
              title="Voice Input"
              description="Speak your prompt instead of typing (Web Speech API; English only)."
              checked={!!beta.voice_input}
              onChange={(v) => save({ beta: { voice_input: v } })}
              testId="toggle-voice-input"
            />
          </div>
        </div>
      )}

      {tab === 'library' && (
        <div>
          <div className="flex items-center gap-2 mb-5">
            <BookOpen className="w-5 h-5 text-violet-400" />
            <div>
              <h4 className="text-sm font-semibold">Prompt Library</h4>
              <p className="text-xs text-zinc-500">Save reusable instructions. They become available as quick-paste chips in the chat composer.</p>
            </div>
          </div>

          <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4 mb-4">
            <input
              type="text"
              placeholder="Title (e.g. 'Tailwind cleanup')"
              value={newPrompt.title}
              onChange={(e) => setNewPrompt({ ...newPrompt, title: e.target.value })}
              data-testid="prompt-lib-title"
              className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 mb-2 focus:outline-none focus:border-violet-500/50"
            />
            <textarea
              placeholder="The full prompt body…"
              value={newPrompt.body}
              onChange={(e) => setNewPrompt({ ...newPrompt, body: e.target.value })}
              rows={3}
              data-testid="prompt-lib-body"
              className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 resize-none focus:outline-none focus:border-violet-500/50"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => {
                  if (!newPrompt.title.trim() || !newPrompt.body.trim()) {
                    toast.error('Both title and body are required');
                    return;
                  }
                  const next = [
                    ...library,
                    { id: Math.random().toString(36).slice(2, 10), title: newPrompt.title.trim(), body: newPrompt.body.trim() },
                  ];
                  save({ prompt_library: next });
                  setNewPrompt({ title: '', body: '' });
                  toast.success('Prompt saved to library');
                }}
                data-testid="prompt-lib-add"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
              >
                <Plus className="w-3.5 h-3.5" /> Save prompt
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {library.length === 0 ? (
              <div className="text-center py-8 text-xs text-zinc-500">No saved prompts yet.</div>
            ) : (
              library.map((p) => (
                <div key={p.id} className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-3" data-testid={`prompt-lib-row-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{p.title}</p>
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{p.body}</p>
                    </div>
                    <button
                      onClick={() => save({ prompt_library: library.filter((x) => x.id !== p.id) })}
                      className="p-1.5 rounded hover:bg-red-500/10 text-zinc-500 hover:text-red-300"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NOTIFICATIONS ───────────────────────────────────────────────────────────
export function NotificationsSection({ settings, save }) {
  const n = settings.notifications || {};
  return (
    <div>
      <SectionHeader icon={Bell} title="Notifications"
        subtitle="Choose when Snagcart Builder pings you while a long build or agent run is in progress." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FeatureRow icon={Bell} title="Toast on build done"
          description="Show a toast when a Vite build finishes (success or failure)."
          checked={!!n.toast_on_build_done}
          onChange={(v) => save({ notifications: { toast_on_build_done: v } })}
          testId="toggle-toast-build" />
        <FeatureRow icon={Bell} title="Toast on agent done"
          description="Notify when the Autonomous Vision agent completes a run."
          checked={!!n.toast_on_agent_done}
          onChange={(v) => save({ notifications: { toast_on_agent_done: v } })}
          testId="toggle-toast-agent" />
        <FeatureRow icon={Bell} title="Toast on chat reply"
          description="Show a toast when the chat AI replies with code edits."
          checked={!!n.toast_on_chat_reply}
          onChange={(v) => save({ notifications: { toast_on_chat_reply: v } })}
          testId="toggle-toast-chat" />
        <FeatureRow icon={Volume2} title="Sound"
          description="Play a soft chime when a long-running task completes."
          checked={!!n.sound_enabled}
          onChange={(v) => save({ notifications: { sound_enabled: v } })}
          testId="toggle-sound" />
      </div>
    </div>
  );
}

// ─── CLOUD PROVIDERS ─────────────────────────────────────────────────────────
import { CLOUD_PROVIDERS } from '../../data/cloudProviders';

const TEXT_MODELS = [
  'gpt-4o-mini', 'gpt-4o', 'gpt-5.2',
  'claude-sonnet-4.5', 'claude-haiku-4.5', 'claude-opus-4.5',
  'gemini-3-flash', 'gemini-3-pro',
];
const VISION_MODELS = ['gpt-4o', 'gpt-5.2', 'claude-sonnet-4.5', 'gemini-3-pro'];

function ProviderRow({ provider, stored, allEnabled, save }) {
  const [draft, setDraft] = useState('');
  const [testing, setTesting] = useState(false);
  // local enable defaults: stored.enabled is true unless explicitly false.
  const localEnabled = stored.enabled !== false;
  const last = stored.last_test_result || null;

  const onSaveKey = (newKey) => {
    if (typeof newKey === 'string' && newKey.startsWith('••••')) return;
    save({
      cloud_providers: {
        keys: {
          [provider.id]: { api_key: newKey, enabled: localEnabled },
        },
      },
    });
  };

  const toggleEnabled = (v) => {
    save({
      cloud_providers: {
        keys: { [provider.id]: { enabled: v } },
      },
    });
  };

  const test = async () => {
    if (!stored.configured && !draft.trim()) {
      toast.error('Enter an API key first');
      return;
    }
    // If user typed a fresh key but hasn't saved yet, save first.
    if (draft.trim()) {
      onSaveKey(draft.trim());
      setDraft('');
      // give the PUT a moment to land
      await new Promise((r) => setTimeout(r, 400));
    }
    setTesting(true);
    const tid = toast.loading(`Testing ${provider.name}…`);
    try {
      const t = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gpt-pilot/settings/cloud-providers/${provider.id}/test`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      const r = data.result || {};
      if (r.ok) toast.success(`${provider.name}: ${r.message || 'Connected'}`, { id: tid });
      else toast.error(`${provider.name}: ${r.message || 'Test failed'}`, { id: tid });
      // Refresh server-side persisted result by re-pulling settings.
      // Quickest path: re-save an empty patch that triggers GET round-trip.
      save({});
    } catch (e) {
      toast.error(`${provider.name}: ${e.message}`, { id: tid });
    } finally {
      setTesting(false);
    }
  };

  const dotColor = !stored.configured
    ? 'bg-zinc-600'
    : last?.ok
      ? 'bg-emerald-400 ring-2 ring-emerald-400/30'
      : last && !last.ok
        ? 'bg-red-400 ring-2 ring-red-400/30'
        : 'bg-amber-400 ring-2 ring-amber-400/30';

  return (
    <div
      data-testid={`cloud-prov-${provider.id}`}
      className={`bg-[#0c1218] border rounded-lg p-4 ${allEnabled && localEnabled ? 'border-white/[0.06]' : 'border-white/[0.04] opacity-70'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`relative w-10 h-10 rounded-md bg-gradient-to-br ${provider.color} flex items-center justify-center text-lg shrink-0 select-none`}>
          <span aria-hidden>{provider.icon}</span>
          <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${dotColor}`} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-white truncate">{provider.name}</h4>
            <span className="text-[10px] uppercase tracking-wide font-semibold text-zinc-300 bg-white/[0.06] px-1.5 py-0.5 rounded">
              {provider.model_count} models
            </span>
            <div className="flex-1" />
            <Toggle
              checked={localEnabled}
              onChange={toggleEnabled}
              testId={`cloud-prov-toggle-${provider.id}`}
              disabled={!allEnabled}
            />
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">{provider.description}</p>

          <div className="mt-3 flex items-stretch gap-2">
            <input
              type="password"
              autoComplete="new-password"
              placeholder={stored.configured ? stored.api_key : `Enter ${provider.name} API key`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid={`cloud-prov-input-${provider.id}`}
              className="flex-1 bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50 font-mono"
            />
            <button
              onClick={() => { if (draft.trim()) { onSaveKey(draft.trim()); setDraft(''); toast.success('Key saved'); } }}
              data-testid={`cloud-prov-save-${provider.id}`}
              disabled={!draft.trim()}
              className="px-3 py-1.5 rounded-md text-xs font-medium border border-white/[0.08] text-zinc-300 hover:text-white hover:bg-white/[0.04] disabled:opacity-40"
              title="Save key"
            >
              Save
            </button>
            <button
              onClick={test}
              data-testid={`cloud-prov-test-${provider.id}`}
              disabled={testing || (!stored.configured && !draft.trim())}
              className="px-3 py-1.5 rounded-md text-xs font-medium border border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-40"
            >
              {testing ? 'Testing…' : 'Test'}
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between text-[11px]">
            <a
              href={provider.getKeyUrl}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`cloud-prov-getkey-${provider.id}`}
              className="text-cyan-300/80 hover:text-cyan-200 inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Get API Key
            </a>
            {last && (
              <span className={`${last.ok ? 'text-emerald-300' : 'text-red-300'} text-[10px]`}>
                {last.ok ? '✓' : '✗'} {last.message}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CloudProvidersSection({ settings, save }) {
  const p = settings.providers || {};
  const cp = settings.cloud_providers || { all_enabled: true, keys: {} };
  const keys = cp.keys || {};
  const allEnabled = cp.all_enabled !== false;

  return (
    <div>
      <SectionHeader icon={Cloud} title="Cloud Providers"
        subtitle="Connect to cloud-based AI models and services. Keys are stored server-side and redacted on read-back." />

      <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4 mb-5 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-white">Enable All Cloud</div>
          <p className="text-xs text-zinc-500 mt-0.5">Master switch — when off, no cloud provider is used regardless of individual toggles.</p>
        </div>
        <Toggle
          checked={allEnabled}
          onChange={(v) => save({ cloud_providers: { all_enabled: v } })}
          testId="cloud-prov-all-toggle"
        />
      </div>

      {/* Default-model selectors stay at the top — they decide which provider's
          model is the primary text/vision endpoint. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4">
          <label className="block text-xs font-medium text-zinc-400 mb-2">Default text model</label>
          <select
            value={p.default_text_model || 'gpt-4o-mini'}
            onChange={(e) => save({ providers: { default_text_model: e.target.value } })}
            data-testid="select-text-model"
            className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
          >
            {TEXT_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4">
          <label className="block text-xs font-medium text-zinc-400 mb-2">Default vision model</label>
          <select
            value={p.default_vision_model || 'gpt-4o'}
            onChange={(e) => save({ providers: { default_vision_model: e.target.value } })}
            data-testid="select-vision-model"
            className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
          >
            {VISION_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3" data-testid="cloud-prov-list">
        {CLOUD_PROVIDERS.map((prov) => {
          const stored = keys[prov.id] || { enabled: true, configured: false, api_key: '' };
          return (
            <ProviderRow
              key={prov.id}
              provider={prov}
              stored={stored}
              allEnabled={allEnabled}
              save={save}
            />
          );
        })}
      </div>

      <p className="text-[11px] text-zinc-600 mt-4">
        Snagcart Builder routes every model call through your configured providers. Keys are validated by hitting each provider's lightweight <code className="text-zinc-400">/models</code> endpoint when you press <span className="text-cyan-300">Test</span>.
      </p>
    </div>
  );
}

// ─── LOCAL PROVIDERS ─────────────────────────────────────────────────────────
export function LocalProvidersSection() {
  return (
    <div>
      <SectionHeader icon={Server} title="Local Providers"
        subtitle="Run Snagcart Builder against a local LLM (Ollama, LM Studio, vLLM)." />
      <div className="bg-[#0c1218] border border-amber-500/20 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-300 bg-amber-500/15 px-2 py-0.5 rounded">
            Beta · not yet active
          </span>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Local-model routing requires that your Ollama / LM Studio server is reachable from
          the Snagcart pod. Because this preview runs in a sandboxed Kubernetes container, an
          inbound tunnel (Cloudflared / ngrok) is needed before we can connect. Configure once
          your tunnel is up and we'll wire it in.
        </p>
      </div>
    </div>
  );
}

// ─── MCP SERVERS ─────────────────────────────────────────────────────────────
export function MCPServersSection({ settings, save }) {
  const servers = settings.mcp_servers || [];
  const [draft, setDraft] = useState({ name: '', endpoint: '' });

  const add = () => {
    if (!draft.name.trim() || !draft.endpoint.trim()) {
      toast.error('Name and endpoint are both required');
      return;
    }
    const next = [
      ...servers,
      { id: Math.random().toString(36).slice(2, 10), name: draft.name.trim(), endpoint: draft.endpoint.trim(), enabled: true },
    ];
    save({ mcp_servers: next });
    setDraft({ name: '', endpoint: '' });
    toast.success('MCP server added');
  };

  return (
    <div>
      <SectionHeader icon={Plug} title="MCP Servers"
        subtitle="Model Context Protocol servers extend the AI's tool-use surface. Register endpoints here." />
      <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input
            type="text"
            placeholder="Server name (e.g. Filesystem)"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="mcp-name"
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50"
          />
          <input
            type="text"
            placeholder="Endpoint URL (https://…)"
            value={draft.endpoint}
            onChange={(e) => setDraft({ ...draft, endpoint: e.target.value })}
            data-testid="mcp-endpoint"
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <div className="flex justify-end">
          <button onClick={add} data-testid="mcp-add"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25">
            <Plus className="w-3.5 h-3.5" /> Add server
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {servers.length === 0 ? (
          <div className="text-center py-8 text-xs text-zinc-500">No MCP servers configured.</div>
        ) : (
          servers.map((s) => (
            <div key={s.id} className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`mcp-row-${s.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{s.name}</p>
                <p className="text-xs text-zinc-500 truncate font-mono">{s.endpoint}</p>
              </div>
              <div className="flex items-center gap-3">
                <Toggle checked={!!s.enabled} onChange={(v) => save({ mcp_servers: servers.map((x) => x.id === s.id ? { ...x, enabled: v } : x) })} testId={`mcp-toggle-${s.id}`} />
                <button onClick={() => save({ mcp_servers: servers.filter((x) => x.id !== s.id) })} className="p-1.5 rounded hover:bg-red-500/10 text-zinc-500 hover:text-red-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── PROJECT MEMORY ──────────────────────────────────────────────────────────
export function ProjectMemorySection({ settings, save }) {
  const pm = settings.project_memory || {};
  const [draft, setDraft] = useState(pm.memory_text || '');
  // Sync draft when settings load asynchronously.
  React.useEffect(() => { setDraft(pm.memory_text || ''); }, [pm.memory_text]);
  return (
    <div>
      <SectionHeader icon={BookOpen} title="Project Memory"
        subtitle="Persistent context the AI keeps in mind across every project you build. Use it for your stack preferences, design tokens or brand voice." />
      <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-white">Use project memory</div>
          <Toggle
            checked={!!pm.enabled}
            onChange={(v) => save({ project_memory: { enabled: v } })}
            testId="toggle-project-memory"
          />
        </div>
        <p className="text-xs text-zinc-500">
          When enabled, the memory text below is prepended to every chat iteration and
          autonomous agent prompt for your projects.
        </p>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={8}
        placeholder="e.g. I'm a solo founder building B2B SaaS dashboards. Always default to dark mode, Inter for body, slate-950 background and violet accents. Prefer Tailwind + shadcn over CSS-in-JS."
        data-testid="project-memory-text"
        className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 resize-none focus:outline-none focus:border-cyan-500/50"
      />
      <div className="mt-3 flex justify-end">
        <button
          onClick={() => { save({ project_memory: { memory_text: draft } }); toast.success('Project memory saved'); }}
          data-testid="save-project-memory"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25"
        >
          <Save className="w-3.5 h-3.5" /> Save memory
        </button>
      </div>
    </div>
  );
}

// ─── SERVICES (GitHub / GitLab / Netlify / Vercel / Supabase) ────────────────
const SERVICE_DEFS = {
  github:   { icon: Github, name: 'GitHub',   blurb: 'Push generated projects to a new repo (planned).', fields: [
    { key: 'token', label: 'Personal Access Token', type: 'password', placeholder: 'ghp_…' },
    { key: 'username', label: 'Username', type: 'text', placeholder: 'octocat' },
  ]},
  gitlab:   { icon: Gitlab, name: 'GitLab',   blurb: 'Mirror builds to a GitLab repository (planned).', fields: [
    { key: 'token', label: 'Personal Access Token', type: 'password', placeholder: 'glpat-…' },
    { key: 'username', label: 'Username', type: 'text', placeholder: '' },
  ]},
  netlify:  { icon: Cloud,  name: 'Netlify',  blurb: 'One-click deploy generated static sites (planned).', fields: [
    { key: 'token', label: 'API Token', type: 'password', placeholder: 'nfp_…' },
    { key: 'site_id', label: 'Site ID (optional)', type: 'text', placeholder: '' },
    { key: 'custom_domain', label: 'Custom domain (optional)', type: 'text', placeholder: 'www.mysite.com' },
  ]},
  vercel:   { icon: Cloud,  name: 'Vercel',   blurb: 'Deploy builds to Vercel (planned).', fields: [
    { key: 'token', label: 'API Token', type: 'password', placeholder: 'verT…' },
    { key: 'team_id', label: 'Team ID (optional)', type: 'text', placeholder: '' },
    { key: 'custom_domain', label: 'Custom domain (optional)', type: 'text', placeholder: 'www.mysite.com' },
  ]},
  supabase: { icon: Box,    name: 'Supabase', blurb: 'Wire generated apps to a Supabase project — client, auth, and RLS migrations.', fields: [
    { key: 'url', label: 'Project URL', type: 'text', placeholder: 'https://xyz.supabase.co' },
    { key: 'anon_key', label: 'Anon Key', type: 'password', placeholder: 'eyJ…' },
    { key: 'service_key', label: 'Service Role Key', type: 'password', placeholder: 'eyJ…' },
  ]},
};

function ServiceCard({ provKey, settings, save }) {
  const def = SERVICE_DEFS[provKey];
  const Icon = def.icon;
  const stored = (settings.services || {})[provKey] || {};
  const [draft, setDraft] = useState({});
  const [expanded, setExpanded] = useState(false);

  const submit = () => {
    const patch = {};
    for (const f of def.fields) {
      if (draft[f.key] !== undefined) patch[f.key] = draft[f.key];
    }
    if (Object.keys(patch).length === 0) {
      toast.error('Enter at least one credential');
      return;
    }
    save({ services: { [provKey]: patch } });
    setDraft({});
    toast.success(`${def.name} credentials saved`);
  };

  const disconnect = () => {
    const wipe = {};
    for (const f of def.fields) wipe[f.key] = '';
    save({ services: { [provKey]: wipe } });
    toast.success(`${def.name} disconnected`);
  };

  return (
    <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg" data-testid={`service-card-${provKey}`}>
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] rounded-lg"
        data-testid={`service-toggle-${provKey}`}
      >
        <div className="w-9 h-9 rounded-md bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
          <Icon className="w-4 h-4 text-zinc-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{def.name}</span>
            {stored.connected ? (
              <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded">
                Connected
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500 bg-white/[0.04] px-1.5 py-0.5 rounded">
                Not connected
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">{def.blurb}</p>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/[0.04] pt-3">
          {def.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[11px] font-medium text-zinc-400 mb-1">{f.label}</label>
              <input
                type={f.type}
                placeholder={stored[f.key] && stored[f.key].startsWith('••••') ? stored[f.key] : f.placeholder}
                value={draft[f.key] ?? ''}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                data-testid={`service-${provKey}-${f.key}`}
                className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            {stored.connected && (
              <button onClick={disconnect} data-testid={`service-${provKey}-disconnect`}
                className="px-3 py-1.5 rounded-md text-xs font-medium border border-red-500/30 text-red-300 hover:bg-red-500/10">
                Disconnect
              </button>
            )}
            <button onClick={submit} data-testid={`service-${provKey}-save`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ServiceSection({ provKey, settings, save }) {
  const def = SERVICE_DEFS[provKey];
  const Icon = def.icon;
  return (
    <div>
      <SectionHeader icon={Icon} title={def.name} subtitle={def.blurb} />
      <ServiceCard provKey={provKey} settings={settings} save={save} />
    </div>
  );
}

// ─── DATA MANAGEMENT ─────────────────────────────────────────────────────────
export function DataManagementSection({ onWiped }) {
  const [summary, setSummary] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/gpt-pilot/data/summary`, { headers: authHeaders() });
        if (res.ok) setSummary(await res.json());
      } catch { /* ignore */ }
    })();
  }, []);

  const exportProjects = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects`, { headers: authHeaders() });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snagcart-projects-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Project metadata exported');
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const wipeAll = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/data/all`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} project(s)`);
      setConfirming(false);
      setSummary({ project_count: 0, disk_bytes: 0, disk_mb: 0 });
      if (onWiped) onWiped();
    } catch (e) {
      toast.error(`Wipe failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SectionHeader icon={Database} title="Data Management"
        subtitle="Export or permanently delete every Snagcart project you've built. Settings and account are not affected." />

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4">
          <div className="text-[11px] text-zinc-500 uppercase tracking-wide">Projects</div>
          <div className="text-2xl font-semibold mt-1" data-testid="data-project-count">{summary?.project_count ?? '—'}</div>
        </div>
        <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4">
          <div className="text-[11px] text-zinc-500 uppercase tracking-wide">Disk usage</div>
          <div className="text-2xl font-semibold mt-1" data-testid="data-disk-mb">{summary ? `${summary.disk_mb} MB` : '—'}</div>
        </div>
      </div>

      <div className="bg-[#0c1218] border border-white/[0.06] rounded-lg p-4 flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-white">Export projects metadata</div>
          <p className="text-xs text-zinc-500 mt-0.5">Download a JSON file with all your project names, statuses and timestamps.</p>
        </div>
        <button onClick={exportProjects} disabled={busy} data-testid="data-export-btn"
          className="px-3 py-1.5 rounded-md text-xs font-medium border border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-50">
          Export
        </button>
      </div>

      <div className="bg-red-950/30 border border-red-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-red-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Danger zone</div>
            <p className="text-xs text-red-300/80 mt-0.5">Permanently delete every Snagcart project + workspace. This cannot be undone.</p>
          </div>
          {!confirming ? (
            <button onClick={() => setConfirming(true)} data-testid="data-wipe-btn"
              className="px-3 py-1.5 rounded-md text-xs font-medium border border-red-500/40 text-red-200 hover:bg-red-500/15">
              Delete all
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)} className="px-3 py-1.5 rounded-md text-xs border border-white/[0.1] text-zinc-300 hover:bg-white/[0.04]">
                Cancel
              </button>
              <button onClick={wipeAll} disabled={busy} data-testid="data-wipe-confirm"
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-red-500 text-white hover:bg-red-400 disabled:opacity-50">
                {busy ? 'Wiping…' : 'Yes, delete everything'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { XIcon };
